/**
 * Every URL the app owns, in one place. The backend's magic-link redirect
 * targets `/app/insights` and `/app/studio/{resumeId}` (API_SPEC.md §2), so
 * routes are real paths, not hashes.
 */
export const ROUTES = {
  landing: '/',
  start: '/start',
  analyzing: '/start/analyzing',
  report: '/report',
  auth: '/auth',
  checkEmail: '/auth/check-email',
  /** Sign-in returns to the Android app (android-app ticket 08): the system browser lands here and hands the session back.
   *  `features/auth/appReturn.ts` (`APP_RETURN_PATH`) holds the same path. */
  appReturn: '/auth/app-return',
  /** The legal hub. Each document has its own top-level path, `legal.documents[].path`
   *  (`lib/legal.ts`): `/terms`, `/privacy`, `/auto-apply-terms`. `/billing-terms` was a
   *  page and now leads to its section of the Terms (`legal.sections`). */
  legal: '/policies',
  insights: '/app/insights',
  search: '/app/search',
  /** Today's shortlist, the swipe deck (swipe-deck ticket 05). */
  shortlist: '/app/shortlist',
  /** Action: every job swiped right on, and what stands between it and sent (action-tab tickets 15 and 17).
   *  It replaced the Ready tray, whose `/app/shortlist/ready` now redirects here (`App.tsx`). */
  action: '/app/action',
  tracking: '/app/tracking',
  resumeHealth: '/app/resume-health',
  studio: '/app/studio',
  tailorHub: '/app/resume-tailor',
  rebuild: '/app/rebuild',
  tailor: '/app/tailor',
  auto: '/app/auto',
  /** The Android app's More tab (android-app ticket 11): the rail's destinations no other tab leads to. */
  more: '/app/more',
  billing: '/app/billing',
  usage: '/app/usage',
  /** Account: a settings list on a phone, a left list and a right panel on desktop (ux-bug-pass-8 ticket 12). */
  account: '/app/account',
  /** Account → Job preferences: target role, places, work style, job type and the account country (ux-bug-pass-8 ticket 12). */
  preferences: '/app/account/preferences',
  /** Account → Login and security: the login email, the sign-in method, member since (ux-bug-pass-8 ticket 12). */
  security: '/app/account/security',
  /** Account → Delete account (ux-bug-pass-8 ticket 12). */
  accountDelete: '/app/account/delete',
  /** Account → Your answers: every banked answer, to see, change or delete (action-tab ticket 21). */
  answers: '/app/account/answers',
  /** Account → Your profile: how complete it is and what each row holds (profile-progress ticket 01). Linked from Account (ticket 11). */
  profile: '/app/account/profile',
  /** Profile → Skill Bank (skill-bank-tailor ticket 05); 404 in the API, so a redirect to Profile, while the setting is off. */
  skillBank: '/app/account/profile/skills',
} as const;

/** A rebuild's Gap questions (compass-health-rebuild ticket 14). The run id is
 *  the only thing in the path — which question is current is the server's, so
 *  there is no step in the URL, and reloading resumes at the next unanswered. */
export function rebuildQuestionsPath(runId: string): string {
  return ROUTES.rebuild + '/' + encodeURIComponent(runId) + '/questions';
}

/** A rebuild's progress screen (ux-bug-pass ticket 21): where both of the
 *  question screen's final CTAs land, and where a reload resumes the poll. Its
 *  own path rather than a state of `/app/rebuild`, so the run id survives a
 *  reload and Back from it returns to the Rebuild page (the question screen
 *  is replaced on the way in). */
export function rebuildProgressPath(runId: string): string {
  return ROUTES.rebuild + '/' + encodeURIComponent(runId) + '/progress';
}

export function jobPath(jobId: string): string {
  return `/app/search/${encodeURIComponent(jobId)}`;
}

/** Ticket 37: the headless-browser PDF export's print-only route
 *  (`features/print/PrintResume.tsx`). Never opened by a person — a
 *  worker's Chromium navigates here with a short-lived token from
 *  `app/services/exports/render.py`. Outside `/app`, so `isProtectedPath`
 *  correctly leaves it unauthenticated. */
export function printResumePath(token: string): string {
  return `/print/resume/${encodeURIComponent(token)}`;
}

/** Ticket 38: the funnel's before/after screenshot render — the same
 *  headless-browser pattern as `printResumePath`, but the token names a
 *  `preview_render_jobs` row (an arbitrary document, not a real resume) and
 *  the caller takes a screenshot (`render_screenshot`) rather than a PDF. */
export function previewResumePath(token: string): string {
  return `/print/preview/${encodeURIComponent(token)}`;
}

export type StudioStep = 'library' | 'editor' | 'complete';
/* Resume Studio owns three URL shapes:
     /app/studio                library
     /app/studio/new | /:id     editor (that resume open; "new" = template picker)
     /app/studio/:id/complete   the completion summary for that resume */
export function studioPath(step: StudioStep, resumeId?: string | null): string {
  const id = resumeId || 'new';
  if (step === 'editor') return '/app/studio/' + encodeURIComponent(id);
  if (step === 'complete') return '/app/studio/' + encodeURIComponent(id) + '/complete';
  return '/app/studio';
}

/** The Resume Editor with its missing-skills panel open (compass-health-rebuild
 *  ticket 11), where Resume Health's "+N more missing" lands. `targetRole` is
 *  the role the sending page scored, when it is not the account's (a New Health
 *  Check's, ticket 09): the panel reads that role's list, or the two screens
 *  would count one role's skills and list another's. */
export const SKILLS_PANEL_PARAM = 'panel';
export const SKILLS_PANEL_VALUE = 'skills';
export const SKILLS_PANEL_ROLE_PARAM = 'targetRole';
export function skillsPanelPath(resumeId: string, targetRole?: string): string {
  const query = new URLSearchParams({ [SKILLS_PANEL_PARAM]: SKILLS_PANEL_VALUE });
  if (targetRole?.trim()) query.set(SKILLS_PANEL_ROLE_PARAM, targetRole.trim());
  return studioPath('editor', resumeId) + '?' + query.toString();
}

export function isProtectedPath(path: string): boolean {
  return path.startsWith('/app');
}

/**
 * The app's front door and its default screen. `RequireAuth` sends a signed-out visit to `/app` as `/auth?next=/app`,
 * and the Android app opens at `/app`, so every first sign-in there carries it. It names no place the person was
 * heading to: the server's landing rule picks one (`auth.rules.landing`: no master résumé, the upload flow; else
 * today's shortlist). Treated as a deep link ("a deep link always wins") it sent a brand-new account to an empty
 * shortlist. A query on the shortlist can name a card, so only the bare paths count. `scripts/check-sign-in-next.mjs`.
 */
const DEFAULT_LANDINGS = new Set(['/app', '/app/', ROUTES.shortlist, ROUTES.shortlist + '/']);

/** Where a sign-in should land afterwards: only a rooted same-origin path is ever forwarded, and not the default home. */
export function safeNext(next: string | null | undefined): string | null {
  if (!next || !next.startsWith('/') || next.startsWith('//') || next.includes('\\')) return null;
  if (DEFAULT_LANDINGS.has(next)) return null;
  return next;
}
